import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  Plus, 
  Search, 
  FolderKanban,
  Calendar,
  Users,
  Edit,
  BarChart3
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Format date from yyyy-mm-dd to dd-mm-yy
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  if (parts.length !== 3) return dateStr;
  const [year, month, day] = parts;
  return `${day}-${month}-${year.slice(-2)}`;
};

const statusConfig = {
  draft: { label: 'Brouillon', styles: 'bg-slate-100 text-slate-700 border-slate-200' },
  active: { label: 'Actif', styles: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  completed: { label: 'Terminé', styles: 'bg-blue-100 text-blue-700 border-blue-200' },
  archived: { label: 'Archivé', styles: 'bg-gray-100 text-gray-500 border-gray-200' }
};

export const ProjectsPage = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newProject, setNewProject] = useState({
    name: '',
    description: '',
    start_date: '',
    end_date: '',
    reporting_period: 'monthly',
    min_hours_per_assignment: '',
    min_interventions_per_assignment: ''
  });
  const [editProject, setEditProject] = useState(null);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des projets');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newProject.name.trim() || !newProject.start_date || !newProject.end_date) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...newProject,
        min_hours_per_assignment: newProject.min_hours_per_assignment ? parseFloat(newProject.min_hours_per_assignment) : 0,
        min_interventions_per_assignment: newProject.min_interventions_per_assignment ? parseInt(newProject.min_interventions_per_assignment) : 0
      };
      await axios.post(`${API}/projects`, payload);
      toast.success('Projet créé avec succès');
      setDialogOpen(false);
      setNewProject({
        name: '',
        description: '',
        start_date: '',
        end_date: '',
        reporting_period: 'monthly',
        min_hours_per_assignment: '',
        min_interventions_per_assignment: ''
      });
      fetchProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenEdit = (project) => {
    setEditProject({
      id: project.id,
      name: project.name,
      description: project.description || '',
      start_date: project.start_date,
      end_date: project.end_date,
      reporting_period: project.reporting_period || 'monthly',
      min_hours_per_assignment: project.min_hours_per_assignment || '',
      min_interventions_per_assignment: project.min_interventions_per_assignment || ''
    });
    setEditDialogOpen(true);
  };

  const handleUpdate = async () => {
    if (!editProject.name.trim() || !editProject.start_date || !editProject.end_date) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/projects/${editProject.id}`, {
        name: editProject.name,
        description: editProject.description,
        start_date: editProject.start_date,
        end_date: editProject.end_date,
        reporting_period: editProject.reporting_period,
        min_hours_per_assignment: editProject.min_hours_per_assignment ? parseFloat(editProject.min_hours_per_assignment) : 0,
        min_interventions_per_assignment: editProject.min_interventions_per_assignment ? parseInt(editProject.min_interventions_per_assignment) : 0
      });
      toast.success('Projet mis à jour avec succès');
      setEditDialogOpen(false);
      setEditProject(null);
      fetchProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (projectId, newStatus) => {
    try {
      await axios.put(`${API}/projects/${projectId}/status?status=${newStatus}`);
      toast.success('Statut mis à jour');
      fetchProjects();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const filteredProjects = projects.filter(p =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin h-8 w-8 border-4 border-slate-900 border-t-transparent rounded-full"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 fade-in">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">Projets</h1>
            <p className="text-slate-500 mt-1">Gérez vos projets et leurs affectations</p>
          </div>
          <Button 
            onClick={() => setDialogOpen(true)}
            className="bg-orange-500 hover:bg-orange-600 text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouveau projet
          </Button>
        </div>

        {/* Search */}
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Rechercher un projet..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </CardContent>
        </Card>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProjects.map((project) => (
            <Card key={project.id} className="border-slate-200 shadow-sm hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-orange-100 rounded-lg">
                      <FolderKanban className="w-5 h-5 text-orange-600" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{project.name}</CardTitle>
                      <CardDescription className="text-sm">
                        {project.description || 'Aucune description'}
                      </CardDescription>
                    </div>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => handleOpenEdit(project)}
                    className="text-slate-500 hover:text-slate-700"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <Badge variant="outline" className={statusConfig[project.status]?.styles}>
                    {statusConfig[project.status]?.label}
                  </Badge>
                  <Select
                    value={project.status}
                    onValueChange={(value) => handleStatusChange(project.id, value)}
                  >
                    <SelectTrigger className="w-32 h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Brouillon</SelectItem>
                      <SelectItem value="active">Actif</SelectItem>
                      <SelectItem value="completed">Terminé</SelectItem>
                      <SelectItem value="archived">Archivé</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Calendar className="w-4 h-4" />
                  <span>{formatDate(project.start_date)} → {formatDate(project.end_date)}</span>
                </div>

                <div className="flex gap-2">
                  <Button asChild variant="outline" size="sm" className="flex-1">
                    <Link to={`/admin/projects/${project.id}`}>
                      <BarChart3 className="w-4 h-4 mr-1" />
                      Dashboard
                    </Link>
                  </Button>
                  <Button asChild variant="outline" size="sm" className="flex-1">
                    <Link to={`/admin/projects/${project.id}/assignments`}>
                      <Users className="w-4 h-4 mr-1" />
                      Affectations
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredProjects.length === 0 && (
          <Card className="border-slate-200">
            <CardContent className="p-12 text-center">
              <FolderKanban className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Aucun projet trouvé</p>
              <Button onClick={() => setDialogOpen(true)} className="mt-4">
                Créer votre premier projet
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Create Project Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Nouveau projet</DialogTitle>
              <DialogDescription>
                Créez un nouveau projet pour organiser vos affectations
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Nom du projet *</Label>
                <Input
                  placeholder="Ex: Programme PME 2025"
                  value={newProject.name}
                  onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  placeholder="Description du projet..."
                  value={newProject.description}
                  onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date de début *</Label>
                  <Input
                    type="date"
                    value={newProject.start_date}
                    onChange={(e) => setNewProject({ ...newProject, start_date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Date de fin *</Label>
                  <Input
                    type="date"
                    value={newProject.end_date}
                    onChange={(e) => setNewProject({ ...newProject, end_date: e.target.value })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Période de reporting</Label>
                <Select
                  value={newProject.reporting_period}
                  onValueChange={(value) => setNewProject({ ...newProject, reporting_period: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly">Hebdomadaire</SelectItem>
                    <SelectItem value="monthly">Mensuel</SelectItem>
                    <SelectItem value="quarterly">Trimestriel</SelectItem>
                    <SelectItem value="annual">Annuel</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Objectifs par affectation */}
              <div className="pt-4 border-t">
                <p className="text-sm font-medium text-slate-700 mb-3">Objectifs par affectation</p>
                <p className="text-xs text-slate-500 mb-3">Ces objectifs seront appliqués à toutes les affectations du projet</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Heures minimum</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.5"
                      placeholder="Ex: 10"
                      value={newProject.min_hours_per_assignment}
                      onChange={(e) => setNewProject({ ...newProject, min_hours_per_assignment: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Interventions minimum</Label>
                    <Input
                      type="number"
                      min="0"
                      placeholder="Ex: 3"
                      value={newProject.min_interventions_per_assignment}
                      onChange={(e) => setNewProject({ ...newProject, min_interventions_per_assignment: e.target.value })}
                    />
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
              <Button onClick={handleCreate} disabled={saving} className="bg-slate-900 hover:bg-slate-800">
                {saving ? 'Création...' : 'Créer le projet'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Project Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Modifier le projet</DialogTitle>
              <DialogDescription>
                Modifiez les détails du projet
              </DialogDescription>
            </DialogHeader>
            {editProject && (
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Nom du projet *</Label>
                  <Input
                    placeholder="Ex: Programme PME 2025"
                    value={editProject.name}
                    onChange={(e) => setEditProject({ ...editProject, name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    placeholder="Description du projet..."
                    value={editProject.description}
                    onChange={(e) => setEditProject({ ...editProject, description: e.target.value })}
                    rows={3}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Date de début *</Label>
                    <Input
                      type="date"
                      value={editProject.start_date}
                      onChange={(e) => setEditProject({ ...editProject, start_date: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Date de fin *</Label>
                    <Input
                      type="date"
                      value={editProject.end_date}
                      onChange={(e) => setEditProject({ ...editProject, end_date: e.target.value })}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Période de reporting</Label>
                  <Select
                    value={editProject.reporting_period}
                    onValueChange={(value) => setEditProject({ ...editProject, reporting_period: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weekly">Hebdomadaire</SelectItem>
                      <SelectItem value="monthly">Mensuel</SelectItem>
                      <SelectItem value="quarterly">Trimestriel</SelectItem>
                      <SelectItem value="annual">Annuel</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Objectifs par affectation */}
                <div className="pt-4 border-t">
                  <p className="text-sm font-medium text-slate-700 mb-3">Objectifs par affectation</p>
                  <p className="text-xs text-slate-500 mb-3">Ces objectifs seront appliqués à toutes les affectations du projet</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Heures minimum</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.5"
                        placeholder="Ex: 10"
                        value={editProject.min_hours_per_assignment}
                        onChange={(e) => setEditProject({ ...editProject, min_hours_per_assignment: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Interventions minimum</Label>
                      <Input
                        type="number"
                        min="0"
                        placeholder="Ex: 3"
                        value={editProject.min_interventions_per_assignment}
                        onChange={(e) => setEditProject({ ...editProject, min_interventions_per_assignment: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Annuler</Button>
              <Button onClick={handleUpdate} disabled={saving} className="bg-slate-900 hover:bg-slate-800">
                {saving ? 'Enregistrement...' : 'Enregistrer les modifications'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};
